import { useMemo, useState } from "react";
import { useTagManagement } from "../dummy_shivam/useTagManagement";
import type { StyleTag } from "../dummy_shivam/index";
import ConfirmationModal from "@/components/ConfirmationModal";

interface EditingTag {
  id: string;
  name: string;
}

const TagManagementPage = () => {
  const { tags, isLoading, error, fetchTags, createTag, updateTag, deleteTag } =
    useTagManagement();

  const [editingTag, setEditingTag] = useState<EditingTag | null>(null);
  const [newTagName, setNewTagName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState<StyleTag | null>(null);

  // Convert slugification to a pure function (no state updates)
  const slugifyTag = (data: string): string => {
    return data
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/_+$/, "")
      .trim();
  };

  // Memoize slugified values to prevent unnecessary calculations
  const newTagSlug = useMemo(() => slugifyTag(newTagName), [newTagName]);

  const editingTagSlug = useMemo(
    () => (editingTag ? slugifyTag(editingTag.name) : ""),
    [editingTag?.name]
  );

  // Filter and sort tags based on search term
  const filteredAndSortedTags = useMemo(() => {
    const validTags = tags.filter((tag) => tag && tag.tag_id && tag.name);

    if (!searchTerm.trim()) {
      return validTags; // Return all tags if no search term
    }

    const searchLower = searchTerm.toLowerCase();

    // Filter tags that match the search term
    const matchedTags = validTags.filter((tag) =>
      tag.name.toLowerCase().includes(searchLower)
    );

    // Sort matched tags: exact matches first, then starts with, then contains
    return matchedTags.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();

      // Exact match gets highest priority
      if (aName === searchLower && bName !== searchLower) return -1;
      if (bName === searchLower && aName !== searchLower) return 1;

      // Starts with gets second priority
      if (aName.startsWith(searchLower) && !bName.startsWith(searchLower))
        return -1;
      if (bName.startsWith(searchLower) && !aName.startsWith(searchLower))
        return 1;

      // Alphabetical order for remaining
      return aName.localeCompare(bName);
    });
  }, [tags, searchTerm]);

  // Create new tag handler
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    setIsCreating(true);
    const created = await createTag(newTagSlug);
    console.log(created);
    if (created) {
      fetchTags();
      setNewTagName("");
    }
    setIsCreating(false);
  };

  // Start editing a tag with validation
  const handleEditStart = (tag: StyleTag) => {
    if (!tag || !tag.tag_id || !tag.name) {
      console.error("Invalid tag data:", tag);
      return;
    }
    setEditingTag({
      id: tag.tag_id,
      name: tag.name,
    });
  };

  // Save edited tag
  const handleEditSave = async () => {
    if (!editingTag || !editingTag.name.trim()) return;

    const updated = await updateTag(editingTag.id, editingTagSlug);
    if (updated) {
      fetchTags();
      setEditingTag(null);
    }
  };

  // Cancel editing
  const handleEditCancel = () => {
    setEditingTag(null);
  };

  // Delete tag handler with validation
  const handleDeleteTag = async (tagId: string) => {
    if (!tagId) {
      console.error("Invalid tag ID for deletion");
      return;
    }
    await deleteTag(tagId);
  };

  // Handle key press events with proper error handling
  const handleKeyPress = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === "Enter") {
      try {
        action();
      } catch (error) {
        console.error("Error in key press handler:", error);
      }
    }
  };

  // Clear search function
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="page">
      <h1 className="text-center bg-white p-3">Tag Management</h1>

      {error && <div className="error-message ">{error}</div>}

      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        id={selectedTag?.tag_id || ""}
        name={selectedTag?.name || ""}
        onDelete={handleDeleteTag}
      />

      <div className="form-section">
        <h2>Create New Tag</h2>
        <div className="create-tag-form">
          <div className="w-full">
            <div className="flex">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => {
                  setNewTagName(e.target.value);
                }}
                onKeyDown={(e) => handleKeyPress(e, handleCreateTag)}
                placeholder="Enter tag name"
                className="tag-input py-2 font-[28px]"
                disabled={isCreating}
              />

              <button
                onClick={handleCreateTag}
                className="!text-gray-900 bg-white border border-gray-300 focus:outline-none cursor-pointer hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-2 py-2 me-2  dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700"
                disabled={!newTagName.trim() || isCreating}
              >
                {/* <Plus size={16} /> */}
                {isCreating ? "Creating..." : "Create Tag"}
              </button>
            </div>
          </div>
        </div>
        {newTagName && (
          <div
            className="tag-slug-preview"
            style={{ marginTop: 8, color: "#888" }}
          >
            Slug: <b>{newTagSlug}</b>
          </div>
        )}
      </div>

      {/* Search Section */}
      <div className="form-section flex justify-end w-full">
        <div className="relative w-[20%]">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search tags..."
            className="px-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Tags List */}
      <div className="form-section">
        <h2>
          Tags ({filteredAndSortedTags.length}
          {searchTerm && filteredAndSortedTags.length !== tags.length && (
            <span className="text-sm text-gray-500">
              {" "}
              of {tags.length} total
            </span>
          )}
          )
          {searchTerm && (
            <span className="text-sm ml-2">
              - Searching for: "
              <span className="font-medium">{searchTerm}</span>"
            </span>
          )}
        </h2>

        {searchTerm && filteredAndSortedTags.length === 0 && (
          <div className="empty-state">
            <p>No tags found matching "{searchTerm}"</p>
            <button
              onClick={handleClearSearch}
              className="text-blue-500 hover:text-blue-700 underline mt-2"
            >
              Clear search to show all tags
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="loading-state">Loading tags...</div>
        ) : filteredAndSortedTags.length === 0 && !searchTerm ? (
          <div className="empty-state">
            <p>No tags found. Create your first tag above.</p>
          </div>
        ) : (
          <div className="tags-grid">
            {filteredAndSortedTags.map((tag) => (
              <div
                key={tag.tag_id}
                className={`tag-card ${
                  searchTerm &&
                  tag.name.toLowerCase().includes(searchTerm.toLowerCase())
                    ? "ring-2 ring-blue-200 bg-blue-50"
                    : ""
                }`}
              >
                {editingTag?.id === tag.tag_id ? (
                  <>
                    <div className="tag-edit-form">
                      <input
                        type="text"
                        value={editingTag.name}
                        onChange={(e) =>
                          setEditingTag({ ...editingTag, name: e.target.value })
                        }
                        onKeyPress={(e) => handleKeyPress(e, handleEditSave)}
                        className="tag-edit-input"
                        autoFocus
                      />
                      <div className="tag-actions">
                        <button
                          onClick={handleEditSave}
                          className="btn-save"
                          disabled={!editingTag.name.trim()}
                          title="Save changes"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleEditCancel}
                          className="btn-cancel"
                          title="Cancel editing"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    <span className="tag-id">
                      Slug: <b>{editingTagSlug}</b>
                    </span>
                  </>
                ) : (
                  <div className="tag-display">
                    <div className="tag-info">
                      <span className="tag-name">
                        {searchTerm ? (
                          // Highlight matching text
                          <HighlightText
                            text={tag.name}
                            searchTerm={searchTerm}
                          />
                        ) : (
                          tag.name || "Unnamed Tag"
                        )}
                      </span>
                      <span className="tag-id">
                        ID: {tag.tag_id || "Unknown"}
                      </span>
                    </div>
                    <div className="tag-actions">
                      <button
                        onClick={() => handleEditStart(tag)}
                        className="btn-edit"
                        title="Edit tag"
                      >
                        Edit
                      </button>
                      <button
                        // onClick={() => handleDeleteTag(tag.tag_id)}
                        onClick={() => {
                          setSelectedTag(tag);
                          setShowDeleteModal(true);
                        }}
                        className="btn-delete"
                        title="Delete tag"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper component to highlight search terms
const HighlightText: React.FC<{ text: string; searchTerm: string }> = ({
  text,
  searchTerm,
}) => {
  if (!searchTerm) return <>{text}</>;

  const regex = new RegExp(`(${searchTerm})`, "gi");
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <mark key={index} className="bg-yellow-200 px-1 rounded">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
};

export default TagManagementPage;
